import { Injectable } from '@angular/core';
import { facetAdaptor, resourceAdaptor } from 'qp-adaptor';
import { ErrorSeverityType } from 'qp-store';
import { SearchService as LibrarySearchService, createSearchService } from 'qp-storefront';
import { AspectRatio, FacetVm, Resource, ResourceVm, SearchRecord } from 'qp-ui-interface';
import { getAppVersion, getDeviceType } from 'qp-utility';
import { NewrelicError } from '../analytics/newrelic/enums/errors';
import { NewrelicService } from '../analytics/newrelic/service/newrelic.service';
import { LocalStorageKeys } from '../shared/LocalStorageKeys';
import { ErrorFactory } from '../shared/modules/errors/handler/ErrorFactory';
import { AdobeService } from '../shared/services/adobe/adobe.service';
import { ConfigService } from '../shared/services/config/config.service';
import { StorefrontPolicyService } from '../shared/services/firstlight/storefront-policy/storefront-policy.service';
import { LocalStorageService } from '../shared/services/local-storage/local-storage.service';
import { getQPHeaders } from '../shared/utils/storefrontUtils';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private searchService: LibrarySearchService;
  constructor(
    private readonly configService: ConfigService,
    private readonly policyService: StorefrontPolicyService,
    private readonly localStorage: LocalStorageService,
    private readonly adobeService: AdobeService,
    private newrelic: NewrelicService
  ) {
    this.initService();
  }

  initService() {
    this.searchService = createSearchService(
      this.configService.applicationConfig.searchHistoryURL,
      this.configService.applicationConfig.noSearchResultsURL,
      this.configService.applicationConfig.vodMetaDataURL,
      this.policyService.getPolicy(),
      true,
      this.configService.applicationConfig.searchHistoryEndpointUrl ?? 'https://search-history-cloud.unow-stag.quickplay.com'
    );
  }

  /**
   * Add recent search item
   *
   * @param recentSearch text
   * @returns
   */
  async addRecentSearch(recentSearch: string): Promise<void> {
    try {
      /* if (!this.userState.isLoggedIn) return;
      const userQPAT = (await this.playbackService.getUserToken() as UserAuthorizationData)?.accessToken;
      if(!userQPAT) return Promise.reject(); */
      return this.searchService.putRecentSearch(getQPHeaders(true, this.localStorage), recentSearch);
    } catch (error) {
      /* const err = ErrorFactory.get(error, NewrelicError.APP_ERROR)
      if ('error' in err) {
        this.newrelic.sendErrorEvent(
          NewrelicError.APP_ERROR,
          err.error.errorCode,
          `error: ${err.error.errorCode}`,
          error.message || 'Failed to search.',
          err,
          ErrorSeverityType.INFO
        )
      } */

      return Promise.reject(error);
    }
  }

  /**
   * Add recent search item
   *
   * @param recentSearch text
   * @returns
   */
  async removeRecentSearch(itemId: string): Promise<void> {
    try {
      /* if (!this.userState.isLoggedIn) return;
      const userQPAT = (await this.playbackService.getUserToken() as UserAuthorizationData)?.accessToken;
      if(!userQPAT) return Promise.reject(); */
      return this.searchService.deleteRecentSearchItem(getQPHeaders(true, this.localStorage), itemId);
    } catch (error) {
      /* const err = ErrorFactory.get(error, NewrelicError.APP_ERROR);
      if ('error' in err) {
        this.newrelic.sendErrorEvent(
          NewrelicError.APP_ERROR,
          err.error.errorCode,
          `error: ${err.error.errorCode}`,
          error.message || 'Failed to  delete search history item.',
          err,
          ErrorSeverityType.INFO
        )
      } */

      return Promise.reject(error);
    }
  }

  /**
   * Get recent searches
   *
   * @param pageNumber
   * @param pageSize
   */
  async getRecentSearches(pageNumber: number = 1, pageSize: number = 5): Promise<SearchRecord[]> {
    try {
      /* if (!this.userState.isLoggedIn) return Promise.reject();
      const userQPAT = (await this.playbackService.getUserToken() as UserAuthorizationData)?.accessToken;
      if(!userQPAT) return Promise.reject(); */
      return this.searchService.getRecentSearch(getQPHeaders(true, this.localStorage), `${pageNumber}`, `${pageSize}`);
    } catch (error) {
      /* const err = ErrorFactory.get(error, NewrelicError.APP_ERROR)
      if ('error' in err) {
        this.newrelic.sendErrorEvent(
          NewrelicError.APP_ERROR,
          err.error.errorCode,
          `error: ${err.error.errorCode}`,
          error.message || 'Failed to fetch recent search.',
          err,
          ErrorSeverityType.INFO
        )
      } */

      return Promise.reject(error);
    }
  }

  public async fetchSearchResults(
    searchTerm: string,
    aspectRatio: AspectRatio,
    contentType?: string,
    pageNumber: string = '1',
    pageSize: string = '50'
  ): Promise<{ resources: ResourceVm[]; facets: FacetVm[] }> {
    try {
      const response = await this.searchService.getSearchResults(searchTerm, pageNumber.toString(), pageSize.toString(), contentType);
      const resources = response.resources
        ? response.resources.map((res: Resource) =>
            resourceAdaptor(
              res,
              this.configService.applicationConfig.imageResizeURL,
              getDeviceType(),
              this.adobeService.fullEntitlementPackages,
              this.localStorage.getItem(LocalStorageKeys.LANG),
              '0-' + aspectRatio,
              null,
              null,
              null,
              getAppVersion()
            )
          )
        : [];
      const facets = response.facet ? facetAdaptor(response.facet, true) : [];
      return { resources, facets };
    } catch (error) {
      const err = ErrorFactory.get(error, NewrelicError.APP_ERROR);
      if ('error' in err) {
        this.newrelic.sendErrorEvent(
          NewrelicError.APP_ERROR,
          err.error.errorCode,
          `HTTP error: ${err.error.errorCode}`,
          error.message || 'Failed to fetch results search.',
          err,
          ErrorSeverityType.WARNING
        );
      }

      return Promise.reject(err);
    }
  }

  /**
   * Get default/popular searches
   *
   * @param pageNumber
   * @param pageSize
   */
  async getPopularSearches(aspectRatio: AspectRatio, pageNumber: number = 1, pageSize: number = 20): Promise<ResourceVm[]> {
    try {
      const response = await this.searchService.getNoSearchResults(`${pageNumber}`, `${pageSize}`);
      return response.resources
        ? response.resources.map((res: Resource) =>
            resourceAdaptor(
              res,
              this.configService.applicationConfig.imageResizeURL,
              getDeviceType(),
              this.adobeService.fullEntitlementPackages,
              this.localStorage.getItem(LocalStorageKeys.LANG),
              '0-' + aspectRatio,
              null,
              null,
              null,
              getAppVersion()
            )
          )
        : [];
    } catch (error) {
      const err = ErrorFactory.get(error, NewrelicError.APP_ERROR);
      if ('error' in err) {
        this.newrelic.sendErrorEvent(
          NewrelicError.APP_ERROR,
          err.error.errorCode,
          `HTTP error: ${err.error.errorCode}`,
          error.message || 'Failed to fetch popular search.',
          err,
          ErrorSeverityType.WARNING
        );
      }

      return Promise.reject(err);
    }
  }
}
